import OfferInclusionsList from 'components/Common/Inclusions/OfferInclusionsList'
import Group from 'components/utils/Group'
import { useOfferInclusions } from 'hooks/useOfferInclusions'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { OFFER_TYPE_ALWAYS_ON } from 'constants/offer'
import CellContent from './CellContent'
import BodyText from 'LuxKit/Typography/BodyText'
import UrgencyLabel from 'LuxKit/Label/UrgencyLabel'
import useOfferMetaData from 'hooks/Offers/useOfferMetaData'
import { useAppSelector } from 'hooks/reduxHooks'
import { buildSearchParamsKey, getShouldUseSearchPrices } from 'lib/search/searchUtils'
import { getHotelSearchBestPackage } from './utils'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'

interface Props {
  offer: App.HotelOffer
  filters: App.OfferListFilters
}

function LEHotelSearchInclusionsCell({
  filters,
  offer,
}: Props) {
  const intl = useIntl()
  const metaData = useOfferMetaData(offer.id, filters)
  const checkIn = filters.checkIn ?? metaData?.suggestedTravelDates?.checkIn
  const checkOut = filters.checkOut ?? metaData?.suggestedTravelDates?.checkOut
  const rooms = filters.rooms

  const searchKey = useMemo(() => buildSearchParamsKey(checkIn, checkOut, rooms), [checkIn, checkOut, rooms])

  const bestMaybeAvailableRate = useAppSelector<App.OfferMaybeAvailableRate | undefined>((state) => state.offer.offerBestPrices[offer.id]?.[searchKey])
  const bestPriceError = useAppSelector<boolean>((state) => !!state.offer.offerPricesErrors[offer.id]?.[searchKey])

  const shouldUseRealSearchPrices = getShouldUseSearchPrices(filters)
  const duration = metaData?.pricing?.duration ?? offer.lowestPricePackage?.duration

  const hasFiltersSet = !!(checkIn && checkOut && rooms?.length)

  const bestPackage = useMemo<App.HotelPackage | undefined>(() => getHotelSearchBestPackage({
    bestPriceError,
    bestMaybeAvailableRate,
    hasFiltersSet,
    metaData,
    offer,
    shouldUseRealSearchPrices,
    duration,
  }), [bestPriceError, bestMaybeAvailableRate, hasFiltersSet, metaData, offer, shouldUseRealSearchPrices, duration])

  const { luxPlusInclusions } = useOfferInclusions(
    bestPackage,
    {
      checkIn: filters?.checkIn,
      checkOut: filters?.checkOut,
    },
  )

  const luxPlusSearchTileInclusions = useMemo<Array<App.PackageInclusion>>(() => {
    if (offer.luxPlusTileInclusions?.length) {
      return offer.luxPlusTileInclusions
    }
    return luxPlusInclusions
  }, [luxPlusInclusions, offer.luxPlusTileInclusions])

  const [
    regularSearchTileFeatures,
    inclusionsTitle,
  ] = useMemo(() => {
    const NoTitle = undefined // urgency label will be used instead

    if (offer.tileInclusions?.length) {
      return [
        offer.tileInclusions,
        offer.tileInclusionsHeading,
      ] as const
    }

    if (offer.lowestPricePackage?.inclusions?.length) {
      return [
        offer.lowestPricePackage.inclusions.filter((inclusion) => inclusion.displayContext === 'Rate'),
        NoTitle,
      ] as const
    }

    if (offer.lowestPricePackage?.roomType.amenities.length) {
      return [
        offer.lowestPricePackage.roomType.amenities,
        NoTitle,
      ] as const
    }

    const pkgWithInclusions = offer.packages.find((p) => p.inclusions?.length)
    return [
      pkgWithInclusions?.inclusions?.filter((inclusion) => inclusion.displayContext === 'Rate'),
      NoTitle,
    ] as const
  }, [offer])

  const showSelectPackageOnlyLabel = offer.type === OFFER_TYPE_ALWAYS_ON

  return <CellContent>
    <VerticalSpacer gap={8}>
      {(!!inclusionsTitle || showSelectPackageOnlyLabel) && <BodyText variant="medium" weight="bold">
        {inclusionsTitle} {showSelectPackageOnlyLabel && <UrgencyLabel variant="positive">{intl.formatMessage({
          id: '0t-WIP3rGs-8SRUorYsn',
          defaultMessage: 'Specific packages only',
          description: 'Urgency label indicating inclusions apply to specific packages only in hotel search offer comparison',
        })}
        </UrgencyLabel>}
      </BodyText>}
      <Group direction="vertical" gap={12}>
        <OfferInclusionsList type="lux-plus" inclusions={luxPlusSearchTileInclusions} />
        {regularSearchTileFeatures && <OfferInclusionsList inclusions={regularSearchTileFeatures} />}
      </Group>
    </VerticalSpacer>
  </CellContent>
}

export default LEHotelSearchInclusionsCell
